import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { alpha, styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { ruRU } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { logout } from './nuiCommon/nuiActions';
import axios from 'axios';

const theme = createTheme(
  ruRU
);

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

function formatFormats(outputFormat) {
  return outputFormat.map(d => d.code).toString();
}

function formatMessages(message) {
  var result = "";
  if (message !== "") {
    let parsed = JSON.parse(message);
    if (Array.isArray(parsed)) {
      parsed.map(m => result = result + m.message + "\n");
    }
  }
  return result
    .replaceAll("<br/>", "")
    .replaceAll("<tt>", "")
    .replaceAll("</tt>", "");
}

function dateFormat(date) {
  return date.format('DD.MM.YYYY');
}

const dateAsStringComparator = (v1, v2) =>
      dayjs(v1, "DD.MM.YYYY hh:mm:ss") - dayjs(v2, "DD.MM.YYYY hh:mm:ss");

const columns = [
  { field: 'date', headerName: 'Дата', width: 180,
    sortComparator: dateAsStringComparator},
  { field: 'user', headerName: 'Логин', width: 180,
    valueGetter: (value, _) => value != null ? value.login : "" },
  { field: 'activityType', headerName: 'Тип действия', width: 120,
    valueGetter: (value, _) => value.name },
  { field: 'year', headerName: 'Год отчета', type: 'string', width: 100,
    valueGetter: (value, _) => value === 0 ? "" : value },
  { field: 'outputFormat', headerName: 'Форматы файлов', width: 200,
    valueGetter: (value, _) => formatFormats(value) },
  { field: 'message', headerName: 'Сообщение', width: '100%',
    valueGetter: (value, _) => formatMessages(value) },
];

export default function NuiAdminConsoleActivity() {
  const [rows, setRows] = React.useState([]);
  const [dateFrom, setDateFrom] = React.useState(dayjs(new Date()).subtract(3, 'day'));
  const [dateTo, setDateTo] = React.useState(dayjs(new Date()));
  const [inProgress, setInProgress] = React.useState(true);

  async function handleTableReload() {
    setInProgress(true);
    await axios.get('/api/activity/list/' +
      dateFormat(dateFrom)
      + '-' +
      dateFormat(dateTo))
      .then((response) => {
        setRows(response.data);
        setInProgress(false);
      })
      .catch((error) => {
        setRows([]);
        console.log(error);
        setInProgress(false);
      });
  }

  React.useEffect(() => {handleTableReload();}, []);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Box sx={{ height: '100%', width: '100%' }}>
          <Stack spacing={2} direction="row"
            sx={{ pb: 2 }}>
            <DatePicker label="Дата с"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <DatePicker label="Дата по"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              slotProps={{ textField: { variant: "standard" } }} />
            <Button variant="contained" sx={{ height: '100%' }}
              onClick={handleTableReload}>
              Обновить
            </Button>
          </Stack>
          <StripedDataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
              sorting: {
                sortModel: [{ field: 'date', sort: 'desc' }],
              }
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            pageSizeOptions={[20, 100]}
            isCellEditable={() => false}
            density="compact"
            loading={inProgress}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'linear-progress',
              },
            }}
          />
          <Button variant="contained"
                  sx={{mt: 2, float: 'right'}}
                  onClick={logout(() => setInProgress(true),
                                  () => setInProgress(false))}>
            Выйти
          </Button>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
